/**
 * The close (×) disc a Dismissible draws for itself — React (web).
 */

/// <reference lib="dom" />

import React from 'react';
import { Icon } from '../Icon/Icon.web';
import {
  AnalyticsActionRestoreProvider,
  useDelegatedAnalyticsAction,
} from '../../analytics/AnalyticsActionContext';
import { ensureFocusVisibleStyle } from '../../elements/focusStyle.web';
import {
  DISMISSIBLE_CLOSE_BACKGROUND,
  DISMISSIBLE_CLOSE_ICON,
  DISMISSIBLE_CLOSE_ICON_COLOR,
  DISMISSIBLE_CLOSE_ICON_SIZE,
  DISMISSIBLE_CLOSE_SIZE,
  type DismissibleCloseProps,
} from './Dismissible';

const CLOSE_FOCUS_STYLE_ID = 'ds-dismissible-close-focus';
const CLOSE_FOCUS_CLASS = 'ds-dismissible-close';

type WebDismissibleCloseProps = DismissibleCloseProps & { onPress: () => void };

/**
 * The dismissal runs on the device and calls no tool, so a GenUI host's blanket
 * analytics suppression would leave its action id unrecorded; capture is
 * restored for this control alone.
 */
export function DismissibleClose(props: WebDismissibleCloseProps) {
  return (
    <AnalyticsActionRestoreProvider>
      <DismissibleCloseInner {...props} />
    </AnalyticsActionRestoreProvider>
  );
}

function DismissibleCloseInner({
  accessibilityLabel,
  actionId,
  testID,
  onPress,
}: WebDismissibleCloseProps) {
  ensureFocusVisibleStyle(CLOSE_FOCUS_STYLE_ID, [`.${CLOSE_FOCUS_CLASS}`]);
  const delegatedAction = useDelegatedAnalyticsAction(actionId);
  return (
    /* analytics-exempt: forwarded-wrapper */
    <button
      type="button"
      className={CLOSE_FOCUS_CLASS}
      aria-label={accessibilityLabel}
      onClick={onPress}
      data-testid={testID}
      data-analytics-action={delegatedAction}
      style={{
        width: DISMISSIBLE_CLOSE_SIZE,
        height: DISMISSIBLE_CLOSE_SIZE,
        borderRadius: DISMISSIBLE_CLOSE_SIZE / 2,
        border: 'none',
        padding: 0,
        cursor: 'pointer',
        backgroundColor: DISMISSIBLE_CLOSE_BACKGROUND,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
      }}
    >
      <Icon
        name={DISMISSIBLE_CLOSE_ICON}
        size={DISMISSIBLE_CLOSE_ICON_SIZE}
        color={DISMISSIBLE_CLOSE_ICON_COLOR}
        aria-hidden
      />
    </button>
  );
}
